/**
 * MD Portal API Client
 * Handles all communication with the backend MD Portal API endpoints
 */

import { authHeaders, handleAuthError } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:7860';
const API_PREFIX = '/api/md';

class MDPortalAPI {
  /**
   * Generic fetch wrapper with error handling + Bearer auth.
   */
  async fetchAPI(endpoint, options = {}) {
    try {
      const response = await fetch(`${API_BASE_URL}${API_PREFIX}${endpoint}`, {
        headers: authHeaders({
          'Content-Type': 'application/json',
          ...options.headers,
        }),
        ...options,
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // ============================================================================
  // Caregivers API (patient-facing; clinician-manageable)
  // Note: these endpoints live under /api/patients, not /api/md.
  // ============================================================================

  async getCaregivers(patientId) {
    const response = await fetch(`${API_BASE_URL}/api/patients/${patientId}/caregivers`, {
      headers: authHeaders({ 'Content-Type': 'application/json' }),
    });
    if (!response.ok) {
      handleAuthError(response);
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async addCaregiver(patientId, { contact, method, display_name, relationship }) {
    const response = await fetch(`${API_BASE_URL}/api/patients/${patientId}/caregivers`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ contact, method, display_name, relationship }),
    });
    if (!response.ok) {
      handleAuthError(response);
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `API Error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async revokeCaregiver(patientId, grantId) {
    const response = await fetch(`${API_BASE_URL}/api/patients/${patientId}/caregivers/${grantId}`, {
      method: 'DELETE',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
    });
    if (!response.ok) {
      handleAuthError(response);
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `API Error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  // ============================================================================
  // Patients API
  // ============================================================================

  /**
   * Get all patients with optional filtering
   */
  async getPatients(filters = {}) {
    const params = new URLSearchParams();
    if (filters.risk_level) params.append('risk_level', filters.risk_level);
    if (filters.search) params.append('search', filters.search);
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.fetchAPI(`/patients${query}`);
  }

  /**
   * Get patient details by ID
   */
  async getPatientDetail(patientId) {
    return this.fetchAPI(`/patients/${patientId}`);
  }

  /**
   * Update patient details (phone, email, care_team)
   * @param {string} patientId - Patient ID
   * @param {Object} updates - Fields to update
   * @param {string} [updates.phone] - Phone number (US format, 10 digits)
   * @param {string} [updates.email] - Email address
   * @param {Object} [updates.care_team] - Care team updates
   * @returns {Promise<Object>} Update result with success status and updated fields
   */
  async updatePatientDetail(patientId, updates) {
    return this.fetchAPI(`/patients/${patientId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Get patient timeline
   */
  async getPatientTimeline(patientId) {
    return this.fetchAPI(`/patients/${patientId}/timeline`);
  }

  // ============================================================================
  // Alerts API
  // ============================================================================

  /**
   * Get all alerts with optional filtering
   */
  async getAlerts(filters = {}) {
    const params = new URLSearchParams();
    if (filters.severity) params.append('severity', filters.severity);
    if (filters.acknowledged !== undefined) params.append('acknowledged', filters.acknowledged);
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.fetchAPI(`/alerts${query}`);
  }

  /**
   * Acknowledge an alert
   */
  async acknowledgeAlert(alertId) {
    return this.fetchAPI(`/alerts/${alertId}/acknowledge`, {
      method: 'POST',
    });
  }

  /**
   * Get alerts summary statistics
   */
  async getAlertsSummary() {
    return this.fetchAPI('/alerts/summary');
  }

  // ============================================================================
  // Wearables API
  // ============================================================================

  /**
   * Get wearable data summary for a patient
   */
  async getWearableSummary(patientId) {
    return this.fetchAPI(`/wearables/${patientId}/summary`);
  }

  /**
   * Get heart rate data
   */
  async getHeartRateData(patientId, period = '7d') {
    return this.fetchAPI(`/wearables/${patientId}/heart-rate?period=${period}`);
  }

  /**
   * Get activity data
   */
  async getActivityData(patientId, period = '7d') {
    return this.fetchAPI(`/wearables/${patientId}/activity?period=${period}`);
  }

  /**
   * Get sleep data
   */
  async getSleepData(patientId, period = '7d') {
    return this.fetchAPI(`/wearables/${patientId}/sleep?period=${period}`);
  }

  /**
   * Get vitals data
   */
  async getVitalsData(patientId) {
    return this.fetchAPI(`/wearables/${patientId}/vitals`);
  }

  /**
   * Get supported device types
   */
  async getSupportedDevices() {
    return this.fetchAPI('/wearables/devices/types');
  }

  // ============================================================================
  // Analytics API
  // ============================================================================

  /**
   * Get population health statistics
   */
  async getPopulationHealth() {
    return this.fetchAPI('/analytics/population-health');
  }

  /**
   * Get risk trends
   */
  async getRiskTrends() {
    return this.fetchAPI('/analytics/risk-trends');
  }

  /**
   * Get preventive care statistics
   */
  async getPreventiveCareStats() {
    return this.fetchAPI('/analytics/preventive-care');
  }

  /**
   * Get activity summary
   */
  async getActivitySummary() {
    return this.fetchAPI('/analytics/activity-summary');
  }

  // ============================================================================
  // Conversations API
  // ============================================================================

  /**
   * Get patient conversations with pagination
   * @param {string} patientId - Patient ID
   * @param {number} limit - Number of conversations to fetch (default: 10)
   */
  async getPatientConversations(patientId, limit = 10) {
    return this.fetchAPI(`/patients/${patientId}/conversations?limit=${limit}`);
  }

  /**
   * Get all conversations needing clinical review
   * Returns conversations flagged by AI for human attention
   */
  async getPendingReviews() {
    return this.fetchAPI('/conversations/pending-review');
  }

  // ============================================================================
  // Concerns API
  // ============================================================================

  /**
   * Get pending concerns with optional severity and type filters
   * @param {string|null} severity - Filter by severity (urgent, high, medium, low)
   * @param {string|null} concernType - Filter by concern type
   */
  async getPendingConcerns(severity = null, concernType = null) {
    const params = new URLSearchParams();
    if (severity) params.append('severity', severity);
    if (concernType) params.append('concern_type', concernType);
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.fetchAPI(`/concerns/pending${query}`);
  }

  /**
   * Resolve a concern with resolution details
   * @param {string} concernId - Concern ID
   * @param {Object} resolution - Resolution data { status, resolved_by, notes }
   */
  async resolveConcern(concernId, resolution) {
    return this.fetchAPI(`/concerns/${concernId}/resolve`, {
      method: 'POST',
      body: JSON.stringify(resolution),
    });
  }

  // ============================================================================
  // Notifications API
  // ============================================================================

  /**
   * Get notification counts for bell icon badge
   * Returns aggregated counts of pending items
   */
  async getNotificationCounts() {
    const [alertsSummary, urgentConcerns, highConcerns] = await Promise.all([
      this.getAlertsSummary(),
      this.getPendingConcerns('urgent'),
      this.getPendingConcerns('high'),
    ]);
    
    return {
      totalAlerts: alertsSummary.unacknowledged_count || 0,
      urgentConcerns: urgentConcerns.length || 0,
      highConcerns: highConcerns.length || 0,
      total: (alertsSummary.unacknowledged_count || 0) +
             (urgentConcerns.length || 0) +
             (highConcerns.length || 0),
    };
  }

  // ============================================================================
  // Discharge Notes & Outbound Calling API
  // ============================================================================

  /**
   * Save discharge notes for a patient
   * @param {string} patientId - Patient ID
   * @param {string} notes - Discharge notes text
   * @param {string} createdBy - Provider name who created the notes
   */
  async saveDischargeNotes(patientId, notes, createdBy) {
    return this.fetchAPI(`/patients/${patientId}/notes`, {
      method: 'POST',
      body: JSON.stringify({
        notes,
        created_by: createdBy,
      }),
    });
  }

  /**
   * Get discharge notes for a patient
   * @param {string} patientId - Patient ID
   */
  async getDischargeNotes(patientId) {
    return this.fetchAPI(`/patients/${patientId}/notes`);
  }

  /**
   * Initiate an outbound call to a patient
   * @param {string} patientId - Patient ID to call
   */
  async initiateOutboundCall(patientId) {
    // Note: This endpoint is under /api/outbound, not /api/md
    const response = await fetch(`${API_BASE_URL}/api/outbound/call`, {
      method: 'POST',
      headers: authHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        patient_id: patientId,
      }),
    });

    if (!response.ok) {
      handleAuthError(response);
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || `API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // ============================================================================
  // Discharge Files API
  // ============================================================================

  /**
   * Get all discharge files for a patient
   * @param {string} patientId - Patient ID
   * @returns {Promise<Array>} Array of file metadata objects
   */
  async getDischargeFiles(patientId) {
    return this.fetchAPI(`/patients/${patientId}/discharge-files`);
  }

  /**
   * Download a discharge file
   * @param {string} patientId - Patient ID
   * @param {string} fileId - File ID to download
   * @returns {Promise<Blob>} File blob for download
   */
  async downloadDischargeFile(patientId, fileId) {
    const response = await fetch(
      `${API_BASE_URL}${API_PREFIX}/patients/${patientId}/discharge-files/${fileId}/download`,
      {
        headers: authHeaders(),
      }
    );

    if (!response.ok) {
      handleAuthError(response);
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.blob();
  }

  /**
   * Delete a discharge file
   * @param {string} patientId - Patient ID
   * @param {string} fileId - File ID to delete
   * @returns {Promise<Object>} Deletion confirmation
   */
  async deleteDischargeFile(patientId, fileId) {
    return this.fetchAPI(`/patients/${patientId}/discharge-files/${fileId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Upload a discharge file
   * Uses the generic /upload endpoint with discharge_planning context
   * @param {string} patientId - Patient ID
   * @param {File} file - File to upload
   * @returns {Promise<Object>} Upload result with file metadata
   */
  async uploadDischargeFile(patientId, file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('patient_id', patientId);
    formData.append('context', 'discharge_planning');

    const response = await fetch(`${API_BASE_URL}/upload`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
      // Don't set Content-Type header - browser will set it with boundary for multipart/form-data
    });

    if (!response.ok) {
      handleAuthError(response);
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Upload failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // ============================================================================
  // Nurse Callback Queue API
  // ============================================================================

  /**
   * Get nurse callback requests with optional filtering
   * @param {Object} filters - Optional filters (status, urgency, patient_id)
   */
  async getNurseCallbacks(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.urgency) params.append('urgency', filters.urgency);
    if (filters.patient_id) params.append('patient_id', filters.patient_id);
    
    const query = params.toString() ? `?${params.toString()}` : '';
    
    // Note: This endpoint is under /api/outbound, not /api/md
    const response = await fetch(`${API_BASE_URL}/api/outbound/nurse-callbacks${query}`, {
      headers: authHeaders({
        'Content-Type': 'application/json',
      }),
    });

    if (!response.ok) {
      handleAuthError(response);
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Complete a nurse callback request
   * @param {string} requestId - Callback request ID
   * @param {string} completedBy - Nurse name who completed the callback
   * @param {string} notes - Notes about the callback
   */
  async completeNurseCallback(requestId, completedBy, notes) {
    // Note: This endpoint is under /api/outbound, not /api/md
    const response = await fetch(`${API_BASE_URL}/api/outbound/nurse-callbacks/${requestId}/complete`, {
      method: 'POST',
      headers: authHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        completed_by: completedBy,
        notes,
      }),
    });

    if (!response.ok) {
      handleAuthError(response);
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || `API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update nurse callback status (e.g., mark as in_progress)
   * @param {string} requestId - Callback request ID
   * @param {string} status - New status (pending, in_progress, completed, cancelled)
   */
  async updateNurseCallbackStatus(requestId, status) {
    // Note: This endpoint is under /api/outbound, not /api/md
    const response = await fetch(`${API_BASE_URL}/api/outbound/nurse-callbacks/${requestId}/status`, {
      method: 'POST',
      headers: authHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        status,
      }),
    });

    if (!response.ok) {
      handleAuthError(response);
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || `API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // ============================================================================
  // Vision AI / Image Analysis API
  // ============================================================================

  /**
   * Analyze an image using Vision AI
   * Sends image to backend for OCR/analysis with specified context
   *
   * @param {File} file - Image file to analyze (JPEG, PNG, WEBP, or PDF)
   * @param {string} context - Analysis context (e.g., 'vaccination_card', 'prescription', 'blood_test_results')
   * @param {string} [patientId] - Optional patient ID for context
   * @returns {Promise<Object>} Analysis result with extracted data
   *
   * Response structure for vaccination_card context:
   * {
   *   detected_content: "Description of what was found",
   *   data: {
   *     patient_info: { name, dob },
   *     vaccinations: [
   *       {
   *         vaccine_type: "COVID-19",
   *         date_administered: "2024-03-10",
   *         dose_label: "1st Dose",
   *         manufacturer: "Pfizer-BioNTech",
   *         lot_number: "ABC123",
   *         healthcare_site: "CVS Pharmacy"
   *       }
   *     ]
   *   },
   *   notes: "Any additional context or caveats"
   * }
   */
  async analyzeImage(file, context, patientId = null) {
    const formData = new FormData();
    formData.append('image', file);  // Backend expects 'image', not 'file'
    formData.append('context', context);
    if (patientId) {
      formData.append('patient_id', patientId);
    }

    // Note: This endpoint is under /api, not /api/md
    const response = await fetch(`${API_BASE_URL}/api/analyze_image`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
      // Don't set Content-Type header - browser will set it with boundary for multipart/form-data
    });

    if (!response.ok) {
      handleAuthError(response);
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || error.detail || `Analysis failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // ============================================================================
  // Vaccination Records API
  // ============================================================================

  /**
   * Add vaccination records to a patient's preventive care data
   *
   * @param {string} patientId - Patient ID
   * @param {Array<Object>} vaccinations - Array of vaccination records to add
   * @param {string} vaccinations[].vaccine_type - Type of vaccine (e.g., "COVID-19", "Influenza")
   * @param {string} vaccinations[].date_administered - Date in YYYY-MM-DD format
   * @param {string} [vaccinations[].dose_label] - Dose label (e.g., "1st Dose", "Booster")
   * @param {string} [vaccinations[].manufacturer] - Vaccine manufacturer
   * @param {string} [vaccinations[].lot_number] - Lot number
   * @param {string} [vaccinations[].healthcare_site] - Where vaccine was administered
   * @param {string} [vaccinations[].next_due] - Next due date in YYYY-MM-DD format
   * @param {string} [vaccinations[].source] - Source of data: 'manual_entry' or 'vision_upload'
   * @param {string} [vaccinations[].source_language] - Language of the source document (e.g., 'English')
   * @returns {Promise<Object>} Update result with success status
   */
  async addPatientVaccinations(patientId, vaccinations) {
    return this.fetchAPI(`/patients/${patientId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        preventive_care: {
          vaccinations: vaccinations,
        },
      }),
    });
  }

  /**
   * Get patient's vaccination history from preventive care data
   *
   * @param {string} patientId - Patient ID
   * @returns {Promise<Array>} Array of vaccination records
   */
  async getPatientVaccinations(patientId) {
    const patient = await this.getPatientDetail(patientId);
    return patient?.preventive_care?.vaccinations || [];
  }

  /**
   * Check for duplicate vaccination records
   * Useful for warning users before adding potentially duplicate entries
   *
   * @param {string} patientId - Patient ID
   * @param {string} vaccineType - Vaccine type to check
   * @param {string} dateAdministered - Date to check (YYYY-MM-DD)
   * @returns {Promise<boolean>} True if a potential duplicate exists
   */
  async checkVaccinationDuplicate(patientId, vaccineType, dateAdministered) {
    const vaccinations = await this.getPatientVaccinations(patientId);
    return vaccinations.some(
      (vax) =>
        vax.vaccine_type === vaccineType &&
        vax.date_administered === dateAdministered
    );
  }

  /**
   * Delete a vaccination record from a patient's preventive care data
   *
   * @param {string} patientId - Patient ID
   * @param {number} vaccinationIndex - Index of the vaccination record to delete
   * @returns {Promise<Object>} Update result with success status
   */
  async deletePatientVaccination(patientId, vaccinationIndex) {
    // First, get the current vaccinations
    const patient = await this.getPatientDetail(patientId);
    const vaccinations = patient?.preventive_care?.vaccinations || [];

    if (vaccinationIndex < 0 || vaccinationIndex >= vaccinations.length) {
      throw new Error('Invalid vaccination index');
    }

    // Remove the vaccination at the specified index
    const updatedVaccinations = vaccinations.filter((_, idx) => idx !== vaccinationIndex);

    // Normalize vaccination data to match expected API schema
    // Some records may have 'date'/'vaccine' instead of 'date_administered'/'vaccine_type'
    const normalizedVaccinations = updatedVaccinations.map(vax => ({
      vaccine_type: vax.vaccine_type || vax.vaccine || 'Unknown',
      date_administered: vax.date_administered || vax.date || null,
      dose_label: vax.dose_label || null,
      manufacturer: vax.manufacturer || null,
      lot_number: vax.lot_number || null,
      healthcare_site: vax.healthcare_site || null,
      next_due: vax.next_due || null,
      source: vax.source || 'manual_entry',
    }));

    // Update the patient record with the normalized vaccinations
    return this.fetchAPI(`/patients/${patientId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        preventive_care: {
          vaccinations: normalizedVaccinations,
        },
      }),
    });
  }

  /**
   * Chat with Luna regarding a specific patient
   * @param {string} patientId - Patient ID
   * @param {Object} chatRequest - Request body with message, active_concern, concern_id, and history
   */
  async chatWithLuna(patientId, chatRequest) {
    return this.fetchAPI(`/patients/${patientId}/chat`, {
      method: 'POST',
      body: JSON.stringify(chatRequest),
    });
  }

}

// Export singleton instance
export const mdApi = new MDPortalAPI();
export default mdApi;